import { useCallback, useEffect, useMemo, useState } from "react";

import { request } from "@/api/client";
import { normalizeUnknownValue } from "@/lib/utils";
import type {
  VenueFeature,
  VenueFeatureCollection,
  VenueGeoSnapshot,
  VenueGeoStats,
  VenueRecord,
} from "@/types/venues";
import { getStateCentroid } from "./stateCentroids";

const DOMESTIC_CODES = new Set(["US", "USA", "UNITED STATES", "UNITED STATES OF AMERICA"]);

const EMPTY_COLLECTION: VenueFeatureCollection = { type: "FeatureCollection", features: [] };

const FEATURE_SOURCE_ID = "crm-venues";

export type VenueDataSource = "crm" | "routing" | "stub";

type SnapshotState = VenueGeoSnapshot & { sourceId: string; dataSource: VenueDataSource };

const FALLBACK_KEYS = ["venues", "items", "data", "results", "records"];

const PLACEHOLDER_COORDINATES = [
  { lat: 39.8283, lon: -98.5795, tolerance: 0.3 },
  { lat: 0, lon: 0, tolerance: 0.2 },
];

const STUB_VENUES: VenueRecord[] = [
  {
    id: "stub-venue-1",
    name: "Sunset Theater",
    city: "Nashville",
    state: "TN",
    country: "US",
    capacity: 1800,
    latitude: 36.1627,
    longitude: -86.7816,
    tags: ["headline", "residency"],
  },
  {
    id: "stub-venue-2",
    name: "Lighthouse Room",
    city: "Los Angeles",
    state: "CA",
    country: "US",
    capacity: 2500,
    latitude: 34.0522,
    longitude: -118.2437,
    tags: ["media", "live-session"],
  },
  {
    id: "stub-venue-3",
    name: "Atlas Hall",
    city: "Toronto",
    state: "ON",
    country: "CA",
    capacity: 3200,
    latitude: 43.6532,
    longitude: -79.3832,
    tags: ["tour-stop"],
  },
];

const parseCoordinate = (value?: number | string | null): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const numeric = Number.parseFloat(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }
  return null;
};

const toVenueArray = (payload: unknown): VenueRecord[] => {
  if (Array.isArray(payload)) {
    return payload as VenueRecord[];
  }
  if (payload && typeof payload === "object") {
    for (const key of FALLBACK_KEYS) {
      const maybe = (payload as Record<string, unknown>)[key];
      if (Array.isArray(maybe)) {
        return maybe as VenueRecord[];
      }
    }
  }
  return [];
};

const dedupeVenues = (venues: VenueRecord[]): VenueRecord[] => {
  const map = new Map<string, VenueRecord>();
  venues.forEach((venue) => {
    if (!venue?.id) return;
    map.set(String(venue.id), venue);
  });
  return Array.from(map.values());
};

const looksLikePlaceholder = (latitude: number, longitude: number) =>
  PLACEHOLDER_COORDINATES.some(
    ({ lat, lon, tolerance }) =>
      Math.abs(latitude - lat) <= tolerance && Math.abs(longitude - lon) <= tolerance,
  );

const determineColor = (isDomestic: boolean, geocodeStatus: "precise" | "approximate") => {
  if (geocodeStatus !== "precise") {
    return "#f87171";
  }
  return isDomestic ? "#34d399" : "#38bdf8";
};

const determineWeight = (capacity?: number | null): number => {
  if (!capacity || !Number.isFinite(capacity)) {
    return 0.9;
  }
  if (capacity >= 4000) return 1.4;
  if (capacity >= 2500) return 1.25;
  if (capacity >= 1200) return 1.1;
  return 0.95;
};

const resolveCoordinates = (
  venue: VenueRecord & { latitude?: number | null; longitude?: number | null },
  isDomestic: boolean,
  stateFallback?: [number, number] | null,
): { coordinates: [number, number]; status: "precise" | "approximate" } | null => {
  const { latitude, longitude } = venue;
  if (
    typeof latitude === "number" &&
    typeof longitude === "number" &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180 &&
    !looksLikePlaceholder(latitude, longitude)
  ) {
    return { coordinates: [longitude, latitude], status: "precise" };
  }

  if (isDomestic && stateFallback) {
    return { coordinates: stateFallback, status: "approximate" };
  }

  return null;
};

const buildSnapshot = (venues: VenueRecord[]): VenueGeoSnapshot => {
  const normalized = dedupeVenues(venues).map((venue) => ({
    ...venue,
    latitude: parseCoordinate(venue.latitude ?? null),
    longitude: parseCoordinate(venue.longitude ?? null),
  }));

  let domestic = 0;
  let international = 0;
  let missingGeocodes = 0;

  const features: VenueFeature[] = [];

  normalized.forEach((venue) => {
    const country = normalizeUnknownValue(venue.country);
    const stateFallback = getStateCentroid(venue.state);
    const isDomestic =
      (!!country && DOMESTIC_CODES.has(country.toUpperCase())) || Boolean(stateFallback);
    if (isDomestic) {
      domestic += 1;
    } else if (country) {
      international += 1;
    }

    const resolved = resolveCoordinates(venue, isDomestic, stateFallback);
    if (!resolved) {
      missingGeocodes += 1;
      return;
    }

    if (resolved.status !== "precise") {
      missingGeocodes += 1;
    }

    const markerColor = determineColor(isDomestic, resolved.status);
    const markerWeight = determineWeight(venue.capacity ?? null);
    features.push({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: resolved.coordinates,
      },
      properties: {
        id: String(venue.id),
        name: normalizeUnknownValue(venue.name) ?? "Unnamed venue",
        city: normalizeUnknownValue(venue.city),
        state: normalizeUnknownValue(venue.state),
        country: country,
        capacity: venue.capacity ?? null,
        tags: Array.isArray(venue.tags) ? venue.tags : null,
        markerColor,
        markerWeight,
        isDomestic,
        geocodeStatus: resolved.status,
      },
    });
  });

  const stats: VenueGeoStats = {
    total: normalized.length,
    domestic,
    international,
    missingGeocodes,
  };

  return {
    venues: normalized,
    featureCollection: { type: "FeatureCollection", features },
    stats,
  };
};

async function fetchVenuesWithFallback(): Promise<{ venues: VenueRecord[]; source: VenueDataSource }> {
  try {
    const crmPayload = await request("/crm/venues");
    const crmVenues = toVenueArray(crmPayload);
    if (crmVenues.length) {
      return { venues: crmVenues, source: "crm" };
    }
  } catch {
    // CRM venues unavailable — fall through to stubs
  }
  return { venues: STUB_VENUES, source: "stub" };
}

export function useVenueGeo() {
  const [snapshot, setSnapshot] = useState<SnapshotState>({
    ...buildSnapshot([]),
    sourceId: FEATURE_SOURCE_ID,
    dataSource: "crm",
    featureCollection: EMPTY_COLLECTION,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { venues, source } = await fetchVenuesWithFallback();
      setSnapshot((prev) => ({
        ...buildSnapshot(venues),
        sourceId: prev.sourceId,
        dataSource: source,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load venues.");
      setSnapshot((prev) => ({
        ...prev,
        featureCollection: EMPTY_COLLECTION,
        stats: { total: 0, domestic: 0, international: 0, missingGeocodes: 0 },
        venues: [],
        dataSource: "crm",
      }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().catch(() => {
      /* handled above */
    });
  }, [load]);

  const memoizedSnapshot = useMemo(
    () => ({
      venues: snapshot.venues,
      featureCollection: snapshot.featureCollection,
      stats: snapshot.stats,
      sourceId: snapshot.sourceId,
      dataSource: snapshot.dataSource,
    }),
    [snapshot.featureCollection, snapshot.stats, snapshot.venues, snapshot.sourceId, snapshot.dataSource],
  );

  return {
    ...memoizedSnapshot,
    loading,
    error,
    reload: load,
  };
}
